import { SupportedCurrency } from '@eagle/core-data-types';
import { Button, List, ListItem, ListItemButton, Menu, MenuItem, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Dialog, DialogTitle } from '@mui/material';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BottomNavigationAction, Icon } from '../../components';
import { useSmallScreen } from '../../hooks';
import { Nullable } from '../../types';
import { useContractListContext } from './contract-list-provider';
import { formatCurrency } from './contract-management.utils';

const CurrencyOptionsList = styled(List)({
  padding: 0,
});

export const ContractListCurrencySwitcher: FC = () => {
  const { state: { selectedCurrency }, changeCurrency, supportedCurrencies } = useContractListContext();
  const isSmallScreen = useSmallScreen();
  const hasSingleCurrency = supportedCurrencies.length === 1;

  if (isSmallScreen) {
    if (hasSingleCurrency) return null;
    return (
      <CurrencySwitcherMobileView
        onChange={changeCurrency}
        options={supportedCurrencies}
        value={selectedCurrency}
      />
    );
  }

  return (
    <CurrencySwitcherDesktopView
      hideButton={hasSingleCurrency}
      onChange={changeCurrency}
      options={supportedCurrencies}
      value={selectedCurrency}
    />
  );
};

interface CurrencySwitcherViewProps {
  value: SupportedCurrency;
  onChange: (value: SupportedCurrency) => void;
  options: SupportedCurrency[];
}

const CurrencySwitcherMobileView: FC<CurrencySwitcherViewProps> = ({ onChange, options, value }) => {
  const { t, i18n } = useTranslation(['common']);
  const [open, setOpen] = useState(false);

  return (
    <>
      <BottomNavigationAction
        data-testid="switch-currency-button"
        onClick={() => { setOpen(true); }}
        label={t('common:page.contract-management.switch-currency-mobile.action', { currency: formatCurrency(i18n, value) })}
        icon={<Icon icon="CurrencyIcon" />}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => { setOpen(false); }}
        open={open}
      >
        <DialogTitle sx={{ px: 2 }}>{t('common:page.contract-management.switch-currency-mobile.title')}</DialogTitle>
        <CurrencyOptionsList>
          {options.map((option) => (
            <ListItem key={option} data-testid={option} disableGutters>
              <ListItemButton
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
              >
                {formatCurrency(i18n, option)}
              </ListItemButton>
            </ListItem>
          ))}
        </CurrencyOptionsList>
      </Dialog>
    </>
  );
};

const CurrencySwitcherDesktopView: FC<CurrencySwitcherViewProps & { hideButton: boolean }> = ({ hideButton, onChange, options, value }) => {
  const { t, i18n } = useTranslation(['common']);
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const open = Boolean(anchorEl);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography data-testid="currency-display" fontStyle="italic">
          <Trans
            i18nKey="common:page.contract-management.prices-shown.labels"
            values={{
              currency: formatCurrency(i18n, value),
            }}
            components={[<strong key={0} />]}
          />
        </Typography>
        {!hideButton && (
          <Button
            data-testid="switch-currency-button"
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
            variant='text'
          >
            {t('common:page.contract-management.switch-currency.action')}
          </Button>
        )}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => { setAnchorEl(null); }}
      >
        {options.map((option) => (
          <MenuItem
            data-testid={option}
            key={option}
            onClick={() => {
              onChange(option);
              setAnchorEl(null);
            }}
          >
            {formatCurrency(i18n, option)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
